/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: eventStore.ts
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  27.09.22 10:13 am.
 * Last modified: 27.09.22 10:13 am.
 *
 * More information at: https://egt-digital.com/
 */
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import * as api from './api';
import { parseBalanceResponse } from './api/balance';
import { copyGameSetting, formatCurrencyValue, getSoundDuration, gridBlocksCount } from './api/helpers';
import constants, {
    FAVORITE_SECTION_NAME,
    LOBBY_SECTION_NAME,
    MOST_PLAYED_SECTION_NAME,
    SCROLL_POS_STORAGE_KEY,
    SEARCH_SECTION_NAME,
} from './constants';
import IBalance, { defaultBalance } from './interfaces/IBalance';
import IExternalChannel from './interfaces/IExternalChannel';
import IGameCurrentPlayers from './interfaces/IGamesCurrentPlayers';
import IGameSettings, { IGroupGame } from './interfaces/IGameSettings';
import IJackpotStat, { IJackpotValues } from './interfaces/IJackpotStat';
import IScrollSectionEvents from './interfaces/IScrollSectionEvents';
import ISearchSectionSettings from './interfaces/ISearchSectionSettings';
import ISectionScrollStorage from './interfaces/ISectionScrollStorage';
import ISectionSettings from './interfaces/ISectionSettings';
import ISettings, { defaultSettings } from './interfaces/ISettings';
import timers from './timers';

const SCROLL_SECTION = new Subject<IScrollSectionEvents>();
const SCROLL_STORAGE = new BehaviorSubject<ISectionScrollStorage>({
    selectedSection: LOBBY_SECTION_NAME,
    sections: {},
    scrollHeight: 0,
});

const SETTINGS = new BehaviorSubject<ISettings>(defaultSettings);

const FAVORITES = new BehaviorSubject<ISectionSettings>({
    name: FAVORITE_SECTION_NAME,
    jackpotName: null,
    icon: 'favorites_icon',
    banner: false,
    games: [],
    gridBlocks: 0,
});

const SEARCH = new BehaviorSubject<ISearchSectionSettings>({
    inputValue: '',
    searchingValues: [],
    name: SEARCH_SECTION_NAME,
    jackpotName: null,
    icon: 'search_icon',
    banner: false,
    games: [],
    gridBlocks: 0,
});

const TRANSLATIONS = new BehaviorSubject<any>({});

const INITIALIZE_SOUNDS = new Subject<any>();
const COMMUNICATION_CHANNEL = new Subject<any>();
const PREV_JACKPOT_STATS = new BehaviorSubject<Map<string, IJackpotStat> | null>(null);
const JACKPOT_STATS = new BehaviorSubject<Map<string, IJackpotStat> | null>(null);
const JACKPOT_STATS_COUNTER = new BehaviorSubject<Map<string, IJackpotStat> | null>(null);
const GAMES_CURRENT_PLAYERS = new BehaviorSubject<IGameCurrentPlayers>({});
const BALANCE = new BehaviorSubject<IBalance>(defaultBalance);
const EXTERNAL_CHANNEL = new Subject<IExternalChannel>();
const SHOW = new BehaviorSubject<boolean>(false); // defines if the lobby is showing or not
const START_GAME = new Subject<string>();
const HOVER_GAME = new Subject<string>();
const ON_CLICK_FAVORITE = new Subject<string>();
const ON_PLAY_SOUND = new Subject<string>();
const SELECT_CATEGORY = new Subject<string>();
const DEFENCE_CODE = new BehaviorSubject<string>('');
const ON_SOUND_MUTED = new BehaviorSubject<boolean>(false);
const SETTINGS_REQUEST_DONE = new BehaviorSubject<boolean>(false);
const ON_ANALYTICS_EVENT = new Subject<{
    gameKey: string;
    banners: { rows: number; cols: number };
    sectionName: string;
    sectionPosition: number;
}>();
const CLEAR_LAST_LOADED_GAME_FROM_LS = new BehaviorSubject<boolean>(true);
const SAVE_GAME_DATA_TO_LOCAL_STORAGE = new Subject<{ key: string; data: string | undefined | null }>();
const ON_BG_MUSIC_MUTED = new BehaviorSubject<boolean>(false);
const DEFAULT_LANG = new BehaviorSubject<{ [key: string]: string }>({});

const store = {
    SHOW,
    SETTINGS,
    BALANCE,
    GAMES_CURRENT_PLAYERS,
    EXTERNAL_CHANNEL,
    FAVORITES,
    SEARCH,
    PREV_JACKPOT_STATS,
    JACKPOT_STATS,
    JACKPOT_STATS_COUNTER,
    START_GAME,
    HOVER_GAME,
    ON_CLICK_FAVORITE,
    ON_PLAY_SOUND,
    SELECT_CATEGORY,
    COMMUNICATION_CHANNEL,
    ON_SOUND_MUTED,
    SETTINGS_REQUEST_DONE,
    TRANSLATIONS,
    INITIALIZE_SOUNDS,
    ON_ANALYTICS_EVENT,
    SAVE_GAME_DATA_TO_LOCAL_STORAGE,
    CLEAR_LAST_LOADED_GAME_FROM_LS,
    ON_BG_MUSIC_MUTED,
    SCROLL_SECTION,
    SCROLL_STORAGE,
    DEFAULT_LANG,
};

TRANSLATIONS.subscribe(({ translationCode }: { translationCode: string }) => {
    if (translationCode) {
        EXTERNAL_CHANNEL.next({
            event: 'language',
            value: translationCode.toLowerCase(),
        });
    }
});

COMMUNICATION_CHANNEL.subscribe(value => {
    if (value.event === 'settings') {
        const settings = api.parseSettingsResponse(value.value);
        SETTINGS.next(settings);
        refreshFavorites();
        updateMostPlayedGames();
        setTimeout(() => SETTINGS_REQUEST_DONE.next(true), 250);
    } else if (value.event === 'jpstats') {
        const jackpotStats = api.parseJackpotResponse(value.value, SETTINGS.value.currency, SETTINGS.value.units);

        PREV_JACKPOT_STATS.next(JACKPOT_STATS.value);
        JACKPOT_STATS.next(jackpotStats);
    } else if (value.event === 'balance') {
        BALANCE.next(parseBalanceResponse(value.balance));
    } else if (value.event === 'display') {
        EXTERNAL_CHANNEL.next(value);
    } else if (value.event === 'exit') {
        store.ON_PLAY_SOUND.next('exitAppSound');
        setTimeout(() => EXTERNAL_CHANNEL.next(value), getSoundDuration('exitAppSound'));
    } else if (value.event === 'mute') {
        ON_SOUND_MUTED.next(Boolean(value));
        if (SHOW.value) {
            EXTERNAL_CHANNEL.next(value);
        }
    } else if (value.event === 'currentPlayers') {
        GAMES_CURRENT_PLAYERS.next(value.currentPlayers);
    }
});

EXTERNAL_CHANNEL.subscribe(({ event, value }) => {
    switch (event) {
        case 'balance':
            COMMUNICATION_CHANNEL.next({
                event: 'balance',
                balance: value,
            });
            break;
        case 'display':
            SHOW.next(Boolean(value));
            break;
        case 'defenceCode':
            if (typeof value === 'string' && value.length) {
                DEFENCE_CODE.next(value);
            } else if (SETTINGS.value.currency.toUpperCase() === 'EGT') {
                // if currency is EGT, means we are in demo mode and
                // if we are in demo mode we might won't have defence code
                // but we won't also need it
                DEFENCE_CODE.next('EGT');
            }
            break;
        case 'mute':
            ON_SOUND_MUTED.next(Boolean(value));
            break;
        case 'bgMusicMute':
            ON_BG_MUSIC_MUTED.next(Boolean(value));
            break;
        default:
            break;
    }
});

let currentIteration = 0;

store.JACKPOT_STATS.subscribe(newJPStatValues => {
    if (!SETTINGS.value) {
        return;
    }
    const units = SETTINGS.value?.units || 100;

    if (!PREV_JACKPOT_STATS.value) {
        JACKPOT_STATS_COUNTER.next(newJPStatValues);
        return;
    }

    if (timers.JP_STAT_UPDATE) {
        clearInterval(timers.JP_STAT_UPDATE);
        currentIteration = 0;
    }

    timers.JP_STAT_UPDATE = setInterval(() => {
        if (!SHOW.value) {
            return;
        }

        currentIteration = currentIteration + 1;
        if (
            currentIteration > constants.JP_STAT_UPDATE_FPS * constants.FETCH_JP_STAT_INTERVAL_SECONDS &&
            timers.JP_STAT_UPDATE
        ) {
            clearInterval(timers.JP_STAT_UPDATE);
            currentIteration = 0;
            return;
        }
        const prevJPStat = PREV_JACKPOT_STATS.value;
        const newJPStatMap = new Map<string, IJackpotStat>();
        prevJPStat?.forEach((jpStat, jpKey) => {
            const newJPStat: IJackpotStat = {
                jackpotName: jpStat.jackpotName,
                minDenomination: jpStat.minDenomination,
                denominations: new Map<number, IJackpotValues[]>(),
                currency: jpStat.currency,
                currencies: [...jpStat.currencies],
            };
            jpStat.denominations.forEach((jpVals, denomKey) => {
                const newJPVals: IJackpotValues[] = [];
                jpVals.forEach((jpVal, idx) => {
                    const targetLevel = jpVal.level;
                    const oldValue =
                        prevJPStat
                            .get(jpKey)
                            ?.denominations.get(denomKey)
                            ?.find(j => j.level === targetLevel)?.value || 0;
                    const targetValue =
                        newJPStatValues
                            ?.get(jpKey)
                            ?.denominations.get(denomKey)
                            ?.find(j => j.level === targetLevel)?.value || 0;

                    const diffValue = Math.floor(
                        ((targetValue - oldValue) /
                            (constants.JP_STAT_UPDATE_FPS * constants.FETCH_JP_STAT_INTERVAL_SECONDS)) *
                            currentIteration,
                    );
                    const value = oldValue + diffValue;

                    const newJPVal: IJackpotValues = {
                        ...jpVal,
                        level: jpVal.level,
                        value,
                        formattedValue: formatCurrencyValue(value, units),
                    };
                    newJPVals.push(newJPVal);
                });
                newJPStat.denominations.set(denomKey, newJPVals);
            });
            newJPStatMap.set(jpKey, newJPStat);
        });
        JACKPOT_STATS_COUNTER.next(newJPStatMap);
    }, Math.floor(1000 / constants.JP_STAT_UPDATE_FPS));
});

const mergeWithOrdersPreservations = (
    mostPlayedSectionGames: IGameSettings[],
    gamesSettings: IGameSettings[],
): IGameSettings[] => {
    const retVal = mostPlayedSectionGames.reduce((acc, curr) => {
        const accOrderIndex = acc.findIndex(game => curr.order === game.order);
        if (accOrderIndex !== -1) {
            acc.splice(accOrderIndex, 0, curr);
        } else {
            acc.push(curr);
        }
        return acc.map(fixOrders);
    }, [...gamesSettings].map(fixOrders));

    return retVal;
};

const fixOrders = (game: IGameSettings, idx: number) => {
    game.order = idx + 1;
    return game;
};

export const updateMostPlayedGames = async () => {
    const { sections, supportedGames } = SETTINGS.value;
    const mostPlayedSection = sections.find(s => s.name === MOST_PLAYED_SECTION_NAME);
    if (!mostPlayedSection) {
        // Back office hasn't configured such section so return
        return;
    }

    const topLimitReached =
        mostPlayedSection?.topLimit && (mostPlayedSection.games.length >= mostPlayedSection?.topLimit ?? 0);
    if (topLimitReached) {
        // FE should not display more games than the topLimit, so there is no need to fetch the dynamic games
        return;
    }

    // Get the dynamic most played games
    const dynamicMostPlayedGames = await api.getDynamicMostPlayedGames();
    // const dynamicMostPlayedGames = [{gameKey: "BCSlot", orderNumber: 1}, {gameKey: "MSHCSlot", orderNumber: 2}, {gameKey: "JWSlot", orderNumber: 3}];

    if (!dynamicMostPlayedGames.length) {
        // No dynamic most played games

        // Still remap the orders to ensure correct visualising
        const sortedMostPlayedGames = mostPlayedSection.games.map(fixOrders).slice(0, mostPlayedSection?.topLimit);

        // Overwrite the Most Played section
        const sortedMostPlayedSection = {
            ...mostPlayedSection,
            games: sortedMostPlayedGames,
            gridBlocks: gridBlocksCount(sortedMostPlayedGames),
        };

        const sortedSectionsCopy = [...SETTINGS.value.sections];
        sortedSectionsCopy[SETTINGS.value.sections.indexOf(mostPlayedSection)] = sortedMostPlayedSection;

        const sortedMostPlayedSettings: ISettings = {
            ...SETTINGS.value,
            sections: sortedSectionsCopy,
            supportedGames: {}, // Delete the data, since we don't need it anymore
        };
        SETTINGS.next(sortedMostPlayedSettings);
        return;
    }

    // Check if game is already included in the Back office/settings response and remove it
    const onlyNewGames = dynamicMostPlayedGames.filter(
        ({ gameKey }) => !mostPlayedSection?.games?.find(g => g.gameKey === gameKey),
    );

    // Check if game is supported in settings/games
    // const supportedGameKeys = ['BCSlot', 'JWSlot'];
    const supportedGameKeys = Object.keys(supportedGames);
    const onlySupportedGames = onlyNewGames.filter(({ gameKey }) => supportedGameKeys.includes(gameKey as string));

    // Generate the needed property fields for the group games
    const groupGames = onlySupportedGames
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map<IGroupGame>((g, i) => {
            return {
                gameKey: g.gameKey,
                order: i + 1,
                imageLobby: 'vertical',
                imageSection: 'vertical',
            } as IGroupGame;
        });

    const gamesSettings: IGameSettings[] = groupGames.map(game =>
        api.parseGameSettings(game, supportedGames[game.gameKey]),
    );

    const newMostPlayedGames = mergeWithOrdersPreservations(mostPlayedSection.games, gamesSettings)
        .map(fixOrders)
        .slice(0, mostPlayedSection?.topLimit);

    // Overwrite the Most Played section
    const newMostPlayedSection = {
        ...mostPlayedSection,
        games: newMostPlayedGames,
        gridBlocks: gridBlocksCount(newMostPlayedGames),
    };

    const sectionsCopy = [...SETTINGS.value.sections];
    sectionsCopy[SETTINGS.value.sections.indexOf(mostPlayedSection)] = newMostPlayedSection;

    const newSettings: ISettings = {
        ...SETTINGS.value,
        sections: sectionsCopy,
        supportedGames: {}, // Delete the data, since we don't need it anymore
    };
    SETTINGS.next(newSettings);
};

export const refreshFavorites = () => {
    let favoritesStorage: string;
    try {
        favoritesStorage =
            localStorage?.getItem(`${constants.LOCAL_STORAGE_FAVORITE_KEY}_${SETTINGS.value.playerId}`) || '';
    } catch (err) {
        favoritesStorage = '';
    }
    const favorites = favoritesStorage.split(',');
    const settings = SETTINGS.value;
    const newFavoriteSection: ISectionSettings = {
        ...FAVORITES.value,
        games: [],
    };
    const pushedToFavoriteSection: string[] = [];

    settings.sections.forEach(section => {
        section.games.forEach(game => {
            if (favorites.includes(game.gameKey) && !pushedToFavoriteSection.includes(game.gameKey)) {
                const newGame = copyGameSetting(game);

                newGame.banners.lobby.rows = 2;
                newGame.banners.lobby.cols = 1;
                newGame.banners.section.rows = 2;
                newGame.banners.section.cols = 1;
                newGame.order = favorites.indexOf(newGame.gameKey) + 1;

                pushedToFavoriteSection.push(game.gameKey);
                newFavoriteSection.games.push(newGame);
            }
        });
    });

    newFavoriteSection.games.sort((a, b) => a.order - b.order);
    newFavoriteSection.gridBlocks = gridBlocksCount(newFavoriteSection.games);

    FAVORITES.next(newFavoriteSection);
};

ON_CLICK_FAVORITE.subscribe(gameKey => {
    if (!SETTINGS.value.playerId) {
        return;
    }

    let favoritesStorage: string;
    try {
        favoritesStorage =
            localStorage?.getItem(`${constants.LOCAL_STORAGE_FAVORITE_KEY}_${SETTINGS.value.playerId}`) || '';
    } catch (err) {
        favoritesStorage = '';
    }
    const favorites = favoritesStorage.split(',');

    const gamePos = favorites.indexOf(gameKey);

    if (gamePos !== -1) {
        favorites.splice(gamePos, 1);
    } else {
        favorites.push(gameKey);
    }
    try {
        localStorage?.setItem(
            `${constants.LOCAL_STORAGE_FAVORITE_KEY}_${SETTINGS.value.playerId}`,
            favorites.filter(fav => Boolean(fav)).join(','),
        );
    } catch (err) {
        // HINT: Leave it empty as we are not going to do anything
    }

    refreshFavorites();
});

store.BALANCE.subscribe(value => {
    const settings = SETTINGS.value;
    if (settings.currency !== value.currency || settings.units !== value.units) {
        const newSettings: ISettings = {
            ...settings,
            currency: value.currency,
            units: value.units,
        };
        SETTINGS.next(newSettings);
    }
});

combineLatest([START_GAME, DEFENCE_CODE]).subscribe(([gameKey, defenceCode]) => {
    const currentGameKey = SETTINGS.value.gameKey;

    if (SETTINGS.value.openType === 'ingame' && currentGameKey === gameKey) {
        // selecting the same game
        EXTERNAL_CHANNEL.next({
            event: 'display',
            value: false,
        });
    } else if (!defenceCode) {
        // selecting different game, but missing defence code
        EXTERNAL_CHANNEL.next({
            event: 'defenceCodeRequired',
            value: gameKey,
        });
    } else {
        SAVE_GAME_DATA_TO_LOCAL_STORAGE.next({
            key: SCROLL_POS_STORAGE_KEY,
            data: JSON.stringify(SCROLL_STORAGE.value),
        });

        // having both gameKey of new game and defence code
        const newUrl = new URL(document.location.href);
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('sessionToken', defenceCode);
        urlParams.set('gameKey', gameKey);
        urlParams.set('language', store.SETTINGS.value.languageCode.toLowerCase());
        urlParams.set('bgMusicMute', ON_BG_MUSIC_MUTED.value ? 'true' : 'false');
        urlParams.set('mute', String(ON_SOUND_MUTED.value));
        let cfl = urlParams.has('comeFromLobby') ? parseInt(urlParams.get('comeFromLobby') || '0', 10) : 0;
        cfl++;
        urlParams.set('comeFromLobby', String(cfl)); // when comming from lobby, setting this to 1 to avoid sending google analytics event
        newUrl.search = urlParams.toString();
        let newHost = newUrl.host;
        const redirectHost = SETTINGS.value.redirectHost;
        if (redirectHost.length) {
            newHost = redirectHost.indexOf('://') !== -1 ? redirectHost.split('://')[1] : redirectHost;
        }
        newUrl.host = newHost;
        newUrl.href = newUrl.href.replace(newUrl.pathname, '');
        store.CLEAR_LAST_LOADED_GAME_FROM_LS.next(false);

        setTimeout(() => {
            const newState: any = { ...window.history.state };
            if (newState?.egtdRedirections) {
                newState.egtdRedirections = newState.egtdRedirections + 1;
                try {
                    window.history.pushState(newState, '', newUrl);
                } catch (pushStateError) {
                    // do nothing when error appear, but expect problems with back/close/exit buttons logic
                }
            }
            document.location.assign(newUrl);
        }, 400); // delay the redirect, so the sound is played
    }
});

ON_ANALYTICS_EVENT.subscribe(data => {
    const gtag = (window as any).gtag;
    if (!gtag) {
        return;
    }

    const settings = SETTINGS.value;

    const fromLobby = settings.selectedSection === LOBBY_SECTION_NAME ? true : false;
    const language = settings.languageCode.toLowerCase();
    const languageChanged = false;
    const tab = settings.selectedSection.toLowerCase();

    let gameImage = 'rectangular';
    if (data.banners.cols === 2) {
        gameImage = 'horizontal';
    } else if (data.banners.rows === 2) {
        gameImage = 'vertical';
    }

    gtag('event', 'openGame', {
        gameKey: data.gameKey,
        fromLobby,
        language,
        languageChanged,
        tab,
        sectionName: data.sectionName,
        sectionPosition: data.sectionPosition,
        gameImage,
    });
});

// sending null or undefined for data clears the local storage
SAVE_GAME_DATA_TO_LOCAL_STORAGE.subscribe(({ key, data }) => {
    try {
        if (data) {
            localStorage?.setItem(key, data);
        } else if (!data && localStorage?.getItem(key)) {
            localStorage?.removeItem(key);
        }
    } catch (err) {
        // HINT: Leave it empty as we are not going to do anything
    }
});

SCROLL_SECTION.subscribe(data => {
    const currStor = SCROLL_STORAGE.value;
    const name = data.name;
    let isDifferent = false;
    if (!currStor.sections?.[name]) {
        currStor.sections[name] = {
            top: 0,
            left: 0,
        };
        isDifferent = true;
    } else if (currStor.sections[name][data.type] !== data.value) {
        isDifferent = true;
    }
    if (isDifferent) {
        currStor.sections[name][data.type] = data.value;
        let scrollHeight = SCROLL_STORAGE.value.scrollHeight;
        if (data.height !== undefined) {
            scrollHeight = data.height;
        }
        SCROLL_STORAGE.next({ ...currStor, scrollHeight });
    }
});

SELECT_CATEGORY.subscribe(categoryName => {
    SCROLL_STORAGE.next({
        ...SCROLL_STORAGE.value,
        selectedSection: categoryName,
    });
});

export default store;
